// Pure type surface for the XMPP layer.
//
// This file is the contract that src/components/** and src/stores/** depend on.
// It declares NO runtime values — only types, interfaces, and one error class.
// The adapter that produces values matching these types lives in ./adapter.ts.
//
// Optional fields use `T | undefined` rather than `T?` so that the adapter can
// always include the field in its return value (consistent with how the
// adapter constructs Message and Chat unconditionally). Combined with
// `exactOptionalPropertyTypes: true` this means callers can freely destructure
// every field without needing `hasOwnProperty` checks.
//
// Tracks REQ-CN-001 in traceability-matrix.md.

// ---------------------------------------------------------------------------
// Opaque re-exports of borogove runtime handles
//
// We expose these so consumers (stores, hooks) can import the *type* of a
// borogove object without each importing `borogove` directly. The borogove
// dependency stays localized to @/lib/xmpp.
//
// `RawClient` is named to make its temporary status visible: once REQ-CN-001.B.4
// (auth) and REQ-CN-001.B.5+ (Chat.tsx) finish, every consumer will be holding
// an adapted `Client` (below) instead of a `RawClient`, and this alias can be
// deleted. `Persistence` / `KeyValueStore` / `IDBPersistence` are opaque on
// purpose — the React tree only stores these handles; it never invokes methods
// on them. If that ever changes, replace the alias with a proper adapter.
// ---------------------------------------------------------------------------

import type * as borogove from "borogove";

export type RawClient = borogove.Client;
/** Opaque alias for the raw `borogove.Chat`. Use the adapter `Chat` (below)
 * for callers that only need the typed surface; `RawChat` is for components
 * mid-migration that still call legacy borogove methods (`.block()`, etc.). */
export type RawChat = borogove.Chat;
/** Opaque alias for the raw `borogove.ChatMessage`. Same migration-bridge
 * role as `RawChat`. */
export type RawMessage = borogove.ChatMessage;
export type Persistence = borogove.Persistence;
export type KeyValueStore = borogove.persistence.KeyValueStore;
export type IDBPersistence = Awaited<ReturnType<typeof borogove.persistence.IDB>>;

/**
 * Opaque alias for `borogove.Register` — the in-progress registration session
 * returned by `startRegistration(server)`. Callers invoke `.submit(formData)`
 * to complete registration. Promoted to a full adapter type when its API
 * surface grows beyond `submit`.
 */
export type RegisterClient = borogove.Register;

/**
 * Opaque alias for `borogove.Form` — the registration form schema returned
 * alongside `RegisterClient`. Callers read `.fields`. Promoted to a full
 * adapter type when its API surface grows beyond field iteration.
 */
export type RegisterForm = borogove.Form;

// ---------------------------------------------------------------------------
// AdapterError
// ---------------------------------------------------------------------------

export class AdapterError extends Error {
	override readonly name = "AdapterError";
	readonly source: unknown;
	constructor(message: string, source?: unknown) {
		super(message);
		this.source = source;
	}
}

// ---------------------------------------------------------------------------
// Reactions
// ---------------------------------------------------------------------------

/**
 * A single reaction to a message. Mirrors borogove_Reaction's runtime shape
 * (senderId, timestamp, key, text) but with timestamp normalized to Unix-ms.
 */
export interface Reaction {
	readonly senderId: string;
	readonly timestamp: number;
	readonly key: string;
	readonly text: string;
}

/**
 * Normalized reactions: emoji → list of reactors. Use `entries` to iterate
 * deterministically; use `size` for total emoji-count. We do not expose a
 * Map directly because borogove's underlying Map has insertion-order
 * dependent iteration that we don't want callers to rely on.
 */
export interface Reactions {
	readonly size: number;
	readonly entries: ReadonlyArray<readonly [emoji: string, reactions: ReadonlyArray<Reaction>]>;
}

// ---------------------------------------------------------------------------
// Message
// ---------------------------------------------------------------------------

export interface ReplyContext {
	readonly id: string;
	readonly body: string;
}

export interface Message {
	/** Local-side ID assigned by the sender's client. */
	readonly localId: string | undefined;
	/** Authoritative ID assigned by the server. */
	readonly serverId: string | undefined;
	/** Identifier of the chat this message belongs to (always property, never method). */
	readonly chatId: string;
	/** Sender JID. Empty string if borogove cannot determine sender. */
	readonly from: string;
	/** Plain-text body. Empty string if absent. */
	readonly text: string;
	/** Unix milliseconds. Borogove's ISO-string timestamps are normalized here. */
	readonly timestamp: number;
	/** True if the message has been retracted (XEP-0424). */
	readonly retracted: boolean;
	/** ID of the message this one corrects (XEP-0308). */
	readonly correctedId: string | undefined;
	/** Reply context (XEP-0461). */
	readonly replyTo: ReplyContext | undefined;
	/** Reactions, or undefined if none. Never an empty Reactions object. */
	readonly reactions: Reactions | undefined;
}

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

export type PresenceState = "online" | "away" | "dnd" | "offline" | "unknown";

export interface PresenceInfo {
	readonly state: PresenceState;
	readonly statusMessage: string | undefined;
	readonly lastSeen: number | undefined;
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export type ChatUiState = "active" | "invited" | "archived" | "unknown";

export interface Chat {
	readonly chatId: string;
	readonly displayName: string;
	readonly isBookmarked: boolean;
	readonly isTrusted: boolean;
	readonly isApp: boolean;
	readonly uiState: ChatUiState;
	readonly lastMessage: Message | undefined;
	readonly unreadCount: number;
	/** Toggle the chat's bookmark state on the server. */
	readonly setBookmarked: (value: boolean) => void;
	/** Signal that all messages up to `messageId` have been displayed. */
	readonly markReadUpTo: (messageId: string) => void;
	/** Get presence info for a participant JID, or undefined if unknown. */
	readonly getParticipantPresence: (jid: string) => PresenceInfo | undefined;
}

// ---------------------------------------------------------------------------
// Listener
// ---------------------------------------------------------------------------

/**
 * Returned from every listener-registration method. Calling unsubscribe()
 * detaches the listener; calling it twice is a no-op.
 */
export interface Listener {
	readonly unsubscribe: () => void;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export type ConnectionStatus = "connecting" | "online" | "offline" | "auth" | "error";

export interface ClientListeners {
	readonly onChatMessage: (cb: (message: Message) => void) => Listener;
	readonly onChatsUpdated: (cb: (chats: ReadonlyArray<Chat>) => void) => Listener;
	readonly onStatusOnline: (cb: () => void) => Listener;
	readonly onStatusOffline: (cb: () => void) => Listener;
	readonly onConnectionFailed: (cb: () => void) => Listener;
	readonly onPasswordNeeded: (cb: () => void) => Listener;
}

export interface Client {
	readonly jid: string;
	readonly getChats: () => ReadonlyArray<Chat>;
	readonly start: () => void;
	readonly usePassword: (password: string) => void;
	readonly subscribePush: (
		reg: ServiceWorkerRegistration,
		pushUrl: string,
		keyPair: CryptoKeyPair,
		expirationDays: number,
	) => Promise<void>;
	readonly listeners: ClientListeners;
}

// ---------------------------------------------------------------------------
// Push notification (decoded service-worker payload)
// ---------------------------------------------------------------------------

/**
 * Decoded push-notification payload from `decodePushPayload`. Mirrors the
 * fields the service worker reads from `borogove.Push.receive()` output, but
 * the `type` field is normalized from borogove's `MessageType` enum to a
 * string union so the service worker doesn't need to import borogove just
 * to compare against `MessageType.MessageCall`.
 */
export interface PushNotification {
	readonly accountId: string;
	readonly chatId: string;
	readonly type: "call" | "chat" | "other";
	readonly title: string;
	readonly body: string | null;
	readonly lang: string | null;
	readonly imageUri: string | null;
	/** ISO 8601 string as emitted by borogove; convert to Date if you need ms. */
	readonly timestamp: string | null;
	readonly callStatus: string | null;
}

// ---------------------------------------------------------------------------
// Message search result
// ---------------------------------------------------------------------------

/**
 * One result of a message search. `message` is the adapted typed Message;
 * `chatName` is the chat's displayName so the UI can render a label without
 * re-resolving the chat.
 */
export interface MessageMatch {
	readonly chatId: string;
	readonly chatName: string;
	readonly message: Message;
}
